import { useState, useCallback, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, Sector } from 'recharts';
import type { ApplicationStatus } from '../types';

interface StatusChartProps {
  data: Record<ApplicationStatus, number>;
  onStatusClick?: (status: ApplicationStatus) => void;
  activeStatus?: ApplicationStatus | null;
}

const STATUS_COLORS: Record<ApplicationStatus, string> = {
  received: '#3b82f6', // blue-500
  enriching: '#eab308', // yellow-500
  enriched: '#22c55e', // green-500
  ai_processing: '#a855f7', // purple-500
  ai_complete: '#6366f1', // indigo-500
  synced: '#10b981', // emerald-500
  error: '#ef4444', // red-500
  stub: '#9ca3af', // gray-400
  skipped: '#9ca3af', // gray-400
};

const STATUS_LABELS: Record<ApplicationStatus, string> = {
  received: 'Received',
  enriching: 'Enriching',
  enriched: 'Enriched',
  ai_processing: 'AI Processing',
  ai_complete: 'AI Complete',
  synced: 'Synced',
  error: 'Error',
  stub: 'Stub',
  skipped: 'Skipped',
};

// Custom active shape for hover effect
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderActiveShape = (props: any) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
  } = props;

  // Detect dark mode via the document element class
  const isDark = document.documentElement.classList.contains('dark');
  const textColor = isDark ? '#e5e7eb' : '#374151';
  const subColor = isDark ? '#9ca3af' : '#6b7280';

  return (
    <g>
      <text x={cx} y={cy - 10} textAnchor="middle" fill={textColor} className="text-sm font-medium">
        {payload.name}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill={subColor} className="text-xs">
        {value} ({(percent * 100).toFixed(0)}%)
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 10}
        outerRadius={outerRadius + 14}
        fill={fill}
      />
    </g>
  );
};

export default function StatusChart({ data, onStatusClick, activeStatus }: StatusChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const [isDark, setIsDark] = useState(false);

  // Track dark mode for inline style elements (tooltip, cell strokes)
  useEffect(() => {
    const checkDark = () => setIsDark(document.documentElement.classList.contains('dark'));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const chartData = Object.entries(data)
    .filter(([_, value]) => value > 0)
    .map(([key, value]) => ({
      name: STATUS_LABELS[key as ApplicationStatus],
      value,
      status: key as ApplicationStatus,
    }));

  const totalApplications = chartData.reduce((sum, item) => sum + item.value, 0);

  // Find index of activeStatus if provided
  const activeStatusIndex = activeStatus
    ? chartData.findIndex((item) => item.status === activeStatus)
    : undefined;

  const onPieEnter = useCallback((_: unknown, index: number) => {
    setActiveIndex(index);
  }, []);

  const onPieLeave = useCallback(() => {
    setActiveIndex(undefined);
  }, []);

  const onPieClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => {
      if (onStatusClick && data?.status) {
        onStatusClick(data.status);
      }
    },
    [onStatusClick]
  );

  if (totalApplications === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-gray-500 dark:text-gray-400">
        No applications to display
      </div>
    );
  }

  // Determine which index should be "active" (hovered or selected)
  const displayActiveIndex = activeIndex ?? activeStatusIndex;

  const tooltipStyle = isDark
    ? { backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '0.5rem', padding: '0.5rem', color: '#e5e7eb' }
    : { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.5rem' };

  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={2}
            dataKey="value"
            {...({ activeIndex: displayActiveIndex, activeShape: renderActiveShape } as any)}
            onMouseEnter={onPieEnter}
            onMouseLeave={onPieLeave}
            onClick={onPieClick}
            style={{ cursor: onStatusClick ? 'pointer' : 'default' }}
          >
            {chartData.map((entry) => (
              <Cell
                key={entry.status}
                fill={STATUS_COLORS[entry.status]}
                stroke={isDark ? '#1f2937' : '#fff'}
                strokeWidth={2}
                opacity={activeStatus && entry.status !== activeStatus ? 0.5 : 1}
              />
            ))}
          </Pie>
          <Tooltip
            contentStyle={tooltipStyle}
            formatter={(value) => [`${value} applications`, 'Count']}
          />
          <Legend
            verticalAlign="bottom"
            height={36}
            onClick={(e) => {
              if (onStatusClick && e.payload) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const payload = e.payload as any;
                if (payload.status) {
                  onStatusClick(payload.status);
                }
              }
            }}
            formatter={(value, entry) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const payload = entry.payload as any;
              const isActive = activeStatus === payload?.status;
              return (
                <span
                  className={`text-sm ${isActive ? 'font-bold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'} ${onStatusClick ? 'cursor-pointer hover:text-gray-900 dark:hover:text-gray-100' : ''}`}
                >
                  {value}
                </span>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      {onStatusClick && (
        <p className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
          Click a segment to filter applications
        </p>
      )}
    </div>
  );
}
